import React, { useState, useMemo } from "react";
import { List, Space, Typography, Button, Checkbox, Divider, Empty, Alert, Input, Modal, message } from "antd";
import { DeleteOutlined, FileTextOutlined, LinkOutlined, ScissorOutlined } from "@ant-design/icons";
import FileGroupCard from "./FileGroupCard";

const { Text } = Typography;

/**
 * 可编辑的文件列表组件
 * 支持文件选择、关联组创建、文件删除、PDF拆分等操作
 */
const EditableFileList = ({ uploadedFiles, fileGroups, onCreateGroup, onEditGroup, onDeleteFile, onRemoveMemberFromGroup, onDisbandGroup, onEditGroupName, onSplitFile, isSubmitting = false }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isGroupModalVisible, setIsGroupModalVisible] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");

    // 计算独立文件（不属于任何关联组的文件）
    const independentFiles = useMemo(() => {
        const allGroupMemberKeys = fileGroups.flatMap((group) => group.memberKeys);
        return uploadedFiles.filter((file) => !allGroupMemberKeys.includes(file.storage_path));
    }, [uploadedFiles, fileGroups]);

    // 处理文件选择
    const handleFileSelect = (fileKey, checked) => {
        setSelectedFiles((prev) => {
            if (checked) {
                return [...prev, fileKey];
            } else {
                return prev.filter((key) => key !== fileKey);
            }
        });
    };

    // 全选/取消全选
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedFiles(independentFiles.map((file) => file.storage_path));
        } else {
            setSelectedFiles([]);
        }
    };

    // 打开创建关联组弹窗
    const handleOpenGroupModal = () => {
        if (selectedFiles.length < 2) {
            message.warning("请至少选择2个文件以创建关联组");
            return;
        }
        // 用选中文件的文件名（去掉扩展名）拼接为默认组名
        const selectedFileObjects = uploadedFiles.filter((file) => selectedFiles.includes(file.storage_path));
        const defaultName = selectedFileObjects
            .map((file) => {
                const name = file.filename || "";
                const lastDot = name.lastIndexOf(".");
                return lastDot > 0 ? name.substring(0, lastDot) : name;
            })
            .filter(Boolean)
            .join(" + ");
        setIsGroupModalVisible(true);
        setNewGroupName(defaultName);
    };

    // 确认创建关联组
    const handleConfirmCreateGroup = () => {
        const trimmedName = newGroupName.trim();
        if (trimmedName && trimmedName.length > 200) {
            message.warning("组名最多200个字符");
            return;
        }
        onCreateGroup(trimmedName || null, selectedFiles);
        setIsGroupModalVisible(false);
        setNewGroupName("");
        setSelectedFiles([]);
    };

    // 取消创建关联组
    const handleCancelCreateGroup = () => {
        setIsGroupModalVisible(false);
        setNewGroupName("");
    };

    // 获取文件图标
    const getFileIcon = (fileName) => {
        const ext = fileName.split(".").pop().toLowerCase();
        if (["pdf"].includes(ext)) return <FileTextOutlined style={{ color: "#f40" }} />;
        if (["jpg", "jpeg", "png"].includes(ext)) return <FileTextOutlined style={{ color: "#52c41a" }} />;
        if (["xls", "xlsx"].includes(ext)) return <FileTextOutlined style={{ color: "#1890ff" }} />;
        return <FileTextOutlined />;
    };

    // 判断文件是否为PDF
    const isPdfFile = (fileName) => {
        const ext = fileName.split(".").pop().toLowerCase();
        return ext === "pdf";
    };

    // 如果没有任何文件
    if (uploadedFiles.length === 0) {
        return <Empty description="暂无已上传文件" style={{ marginTop: 24 }} />;
    }

    return (
        <div>
            {/* 关联文件组列表 */}
            {fileGroups.length > 0 && (
                <>
                    <Divider orientation="left">关联文件组</Divider>
                    {fileGroups.map((group) => (
                        <FileGroupCard key={group.id} group={group} files={uploadedFiles} onRemoveMember={onRemoveMemberFromGroup} onDisbandGroup={onDisbandGroup} onEditName={onEditGroupName} isSubmitting={isSubmitting} />
                    ))}
                </>
            )}

            {/* 独立文件列表 */}
            {independentFiles.length > 0 && (
                <>
                    <Divider orientation="left">
                        <Space>
                            独立文件
                            {independentFiles.length >= 2 && (
                                <Checkbox checked={selectedFiles.length === independentFiles.length} indeterminate={selectedFiles.length > 0 && selectedFiles.length < independentFiles.length} onChange={(e) => handleSelectAll(e.target.checked)} disabled={isSubmitting}>
                                    全选
                                </Checkbox>
                            )}
                        </Space>
                    </Divider>

                    {/* 批量操作工具栏 */}
                    {selectedFiles.length >= 2 && (
                        <Alert
                            message={
                                <Space>
                                    <Text>已选择 {selectedFiles.length} 个文件</Text>
                                    <Button type="primary" size="small" icon={<LinkOutlined />} onClick={handleOpenGroupModal} disabled={isSubmitting}>
                                        标记为关联组
                                    </Button>
                                    <Button size="small" onClick={() => setSelectedFiles([])} disabled={isSubmitting}>
                                        取消选择
                                    </Button>
                                </Space>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <List
                        size="small"
                        dataSource={independentFiles}
                        renderItem={(file) => {
                            const isSelected = selectedFiles.includes(file.storage_path);
                            return (
                                <List.Item
                                    style={{
                                        backgroundColor: isSelected ? "#e6f7ff" : "transparent",
                                        padding: "8px 12px",
                                        borderRadius: 4,
                                    }}
                                    actions={[
                                        ...(isPdfFile(file.filename) && onSplitFile
                                            ? [
                                                  <Button type="text" size="small" icon={<ScissorOutlined />} onClick={() => onSplitFile(file)} disabled={isSubmitting}>
                                                      拆分
                                                  </Button>,
                                              ]
                                            : []),
                                        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDeleteFile(file)} disabled={isSubmitting}>
                                            删除
                                        </Button>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <Space>
                                                {independentFiles.length >= 2 && <Checkbox checked={isSelected} onChange={(e) => handleFileSelect(file.storage_path, e.target.checked)} disabled={isSubmitting} />}
                                                {getFileIcon(file.filename)}
                                            </Space>
                                        }
                                        title={
                                            <Space size={4}>
                                                <Text>{file.filename}</Text>
                                                {/* 显示"已拆分"标识 */}
                                                {file.has_been_split && <ScissorOutlined style={{ color: "#fa8c16", fontSize: 14 }} title="此文件已被拆分" />}
                                                {/* 显示拆分子文件标识 */}
                                                {file.is_split && <ScissorOutlined style={{ color: "#52c41a", fontSize: 14 }} title={`来自: ${file.split_from_filename}`} />}
                                            </Space>
                                        }
                                        description={
                                            <Space>
                                                <Text type="secondary">{(file.file_size / 1024 / 1024).toFixed(2)} MB</Text>
                                                {/* 显示PDF页数 */}
                                                {file.page_count && <Text type={file.page_count > 10 ? "danger" : file.page_count > 5 ? "warning" : "secondary"}>{file.page_count} 页</Text>}
                                                <Text type="success">上传成功</Text>
                                                {/* 拆分子文件的额外描述 */}
                                                {file.is_split && file.split_pages && <Text type="secondary">第{file.split_pages}页</Text>}
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                </>
            )}

            {/* 创建关联组弹窗 */}
            <Modal title="创建文件关联组" open={isGroupModalVisible} onOk={handleConfirmCreateGroup} onCancel={handleCancelCreateGroup} okText="创建" cancelText="取消" width={500}>
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Alert message={`将要关联 ${selectedFiles.length} 个文件，这些文件将被标记为属于同一笔业务账单。`} type="info" showIcon />

                    <div style={{ marginTop: 16 }}>
                        <Text strong>组名称（可选）：</Text>
                        <Input placeholder="例如：10月燃油费账单" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} maxLength={200} showCount style={{ marginTop: 8 }} />
                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                            留空将自动使用默认名称
                        </Text>
                    </div>

                    <div style={{ marginTop: 16 }}>
                        <Text strong>将要关联的文件：</Text>
                        <List
                            size="small"
                            style={{ marginTop: 8 }}
                            dataSource={uploadedFiles.filter((file) => selectedFiles.includes(file.storage_path))}
                            renderItem={(file) => (
                                <List.Item>
                                    <Space>
                                        {getFileIcon(file.filename)}
                                        <Text>{file.filename}</Text>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </div>
                </Space>
            </Modal>
        </div>
    );
};

export default EditableFileList;
